import { useLightMode } from '@/hooks/useLightMode'
import { Trans, useTranslation } from 'react-i18next'

import { Box, Text, VStack } from '@chakra-ui/react'

import { tVar } from '@/utils/i18n'

import { CheckpointIProps } from '../../types/types'
import { PRINT_CONFIG } from '../utils/bulkPrint'
import { PagePreview, PrintContent } from './BulkPrintPreview'

interface BulkPrintBodyIProps {
  checkpoints: CheckpointIProps[]
  printRef: React.RefObject<HTMLDivElement>
  totalPages: number
}

/**
 * The instructional body of the bulk QR print flow, shared by BulkPrintModal
 * and its dark twin so the copy and the print-ready node exist once. Colours
 * are mode-keyed, so it reads correctly inside either chrome.
 */
export const BulkPrintBody = ({
  checkpoints,
  printRef,
  totalPages,
}: BulkPrintBodyIProps) => {
  const { t } = useTranslation()
  const mode = useLightMode()

  return (
    <>
      {/* Print Info */}
      <Box mb={4}>
        <Text color={`text.secondary.${mode}`} fontSize='sm'>
          {tVar(t, 'floors:print_selected_count', {
            count: checkpoints.length,
          })}
          {totalPages > 1
            ? ` ${tVar(t, 'floors:print_page_total', { count: totalPages })}`
            : ''}
        </Text>
        <Text color={`text.tertiary.${mode}`} fontSize='xs' mt={1}>
          {tVar(t, 'floors:print_grid_summary', {
            columns: PRINT_CONFIG.columns,
            rows: PRINT_CONFIG.rowsPerPage,
          })}
        </Text>
      </Box>

      {/* Option 1: HiveWatch prints */}
      <Box mb={4}>
        <Text color={`text.primary.${mode}`} fontSize='sm' fontWeight='600'>
          {t('floors:print_option_ship_title')}
        </Text>
        <Text color={`text.secondary.${mode}`} fontSize='sm' mt={1}>
          <Trans
            components={{
              email: (
                <Text as='span' color={`text.link.${mode}`} fontWeight='500' />
              ),
            }}
            i18nKey={'floors:print_option_ship_body' as never}
          />
        </Text>
      </Box>

      {/* Option 2: Print yourself */}
      <Box mb={4}>
        <Text color={`text.primary.${mode}`} fontSize='sm' fontWeight='600'>
          {t('floors:print_option_self_title')}
        </Text>
        <Text color={`text.secondary.${mode}`} fontSize='sm' mt={1}>
          <Trans
            components={{ product: <Text as='span' fontWeight='500' /> }}
            i18nKey={'floors:print_option_self_body' as never}
          />
        </Text>

        <Box color={`text.secondary.${mode}`} fontSize='sm' mt={3}>
          <Text fontWeight='500' mb={1}>
            {t('floors:print_before_you_print')}
          </Text>
          <VStack align='start' ml={2} spacing={1}>
            <Text>
              •{' '}
              <Trans
                components={{ lead: <strong /> }}
                i18nKey={'floors:print_tip_laser_only' as never}
              />
            </Text>
            <Text>
              •{' '}
              <Trans
                components={{ lead: <strong /> }}
                i18nKey={'floors:print_tip_test_page' as never}
              />
            </Text>
          </VStack>
        </Box>

        <Box color={`text.secondary.${mode}`} fontSize='sm' mt={3}>
          <Text fontWeight='500' mb={1}>
            {t('floors:print_settings_title')}
          </Text>
          <VStack align='start' ml={2} spacing={0}>
            <Text>• {t('floors:print_setting_paper_size')}</Text>
            <Text>• {t('floors:print_setting_scale')}</Text>
            <Text>• {t('floors:print_setting_margins')}</Text>
            <Text>• {t('floors:print_setting_headers')}</Text>
          </VStack>
        </Box>
      </Box>

      {/* Preview - US Letter page simulation */}
      <Box
        bg={`bg.card.${mode}`}
        border='1px solid'
        borderColor={`border.default.${mode}`}
        borderRadius='md'
        maxH='450px'
        overflowY='auto'
        p={4}
      >
        <Text color={`text.secondary.${mode}`} fontSize='sm' mb={3}>
          {t('floors:print_preview_label')}
        </Text>
        <PagePreview checkpoints={checkpoints} />
      </Box>

      {/* Hidden print content */}
      <Box left='-9999px' position='absolute' top='-9999px'>
        <Box ref={printRef}>
          <PrintContent checkpoints={checkpoints} />
        </Box>
      </Box>
    </>
  )
}
